import React, { useState } from 'react';
import { ArrowLeft, Send, Mail, Building2, User, MessageSquare, CheckCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-5">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#00FFD1]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-[#00FFD1]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Message Sent Successfully
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Thank you for reaching out. Our team will get back to you within 24 hours.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#00FFD1] text-black px-8 py-4 text-lg font-medium hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] transition-all duration-400"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Grid Pattern Overlay */}
      <div 
        className="fixed inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 60px),
            repeating-linear-gradient(-90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 1px, transparent 1px, transparent 60px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Gradient Orbs */}
      <div className="fixed top-1/4 right-1/4 w-[500px] h-[500px] bg-[#00FFD1]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="fixed bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#6B5BFF]/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
          <div className="flex items-center justify-between h-20">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 text-white/60 hover:text-white transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg">Back to Home</span>
            </button>
            <img
              src="https://customer-assets.emergentagent.com/job_94f5a94a-682d-481e-b75c-a692290dbeab/artifacts/ksy0u9nl_Logo.png"
              alt="Prysh AI Labs"
              className="h-10 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-32 pb-20 px-5 md:px-[7.6923%]">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Content */}
            <div className="space-y-8">
              <div>
                <span className="text-[#00FFD1] text-lg font-medium tracking-wide">Get in Touch</span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mt-4">
                  Let's Build
                  <span className="block text-[#00FFD1]">Something Great</span>
                </h1>
              </div>

              <p className="text-xl text-white/60 leading-relaxed">
                Ready to transform your content production? Our team is here to help you get started with Prysh AI.
              </p>

              <div className="space-y-6 pt-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#00FFD1]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Email Us</h3>
                    <a href="mailto:info@pryshai.com" className="text-[#00FFD1] hover:text-[#00FFD1]/80 transition-colors">info@pryshai.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-[#00FFD1]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Enterprise Solutions</h3>
                    <p className="text-white/50">Custom integrations and dedicated support</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-[#121212] border border-white/10 p-8 md:p-10">
              <h2 className="text-2xl font-semibold text-white mb-2">Send us a message</h2>
              <p className="text-white/50 mb-8">We'll get back to you within 24 hours</p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div className="space-y-2">
                  <label className="text-white/70 text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="w-full bg-black border border-white/10 text-white px-4 py-4 text-lg focus:outline-none focus:border-[#00FFD1]/50 transition-colors duration-300 placeholder:text-white/30"
                  />
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label className="text-white/70 text-sm font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                    className="w-full bg-black border border-white/10 text-white px-4 py-4 text-lg focus:outline-none focus:border-[#00FFD1]/50 transition-colors duration-300 placeholder:text-white/30"
                  />
                </div>

                {/* Company Field */}
                <div className="space-y-2">
                  <label className="text-white/70 text-sm font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    className="w-full bg-black border border-white/10 text-white px-4 py-4 text-lg focus:outline-none focus:border-[#00FFD1]/50 transition-colors duration-300 placeholder:text-white/30"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <label className="text-white/70 text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Your Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us about your content needs..."
                    className="w-full bg-black border border-white/10 text-white px-4 py-4 text-lg focus:outline-none focus:border-[#00FFD1]/50 transition-colors duration-300 placeholder:text-white/30 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00FFD1] text-black px-8 py-4 text-lg font-medium hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] transition-all duration-400 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-5 md:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © 2026 Prysh AI Private Limited. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-white/40 hover:text-[#00FFD1] text-sm transition-colors">Privacy</a>
            <a href="#" className="text-white/40 hover:text-[#00FFD1] text-sm transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
